import { FC, useMemo } from 'react';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import {
  CAP_TABLE_NEEDS_MORE_SECURITIES_TAGLINE,
  CAP_TABLE_NEEDS_MORE_SECURITIES_TITLE,
} from 'common/constants/cap-table';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { FUND_OWNERSHIP_SPREADSHEET_TABLE_TERMS } from 'common/constants/fund-ownership';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { UseFormatValues } from 'common/types/format';
import { CustomScalarSpreadsheet, EmptyTableMessage } from 'components';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { useLayoutContext } from 'context/LayoutContext';
import { EmptyCapTableMessage } from 'pages/CapTable/cap-table/components';
import {
  ALLOCATION_EMPTY_FUND_OWNERSHIP_TAGLINE,
  ALLOCATION_EMPTY_FUND_OWNERSHIP_TITLE,
} from 'pages/ValuationsAllocation/common/constants/allocation';
import { EQUITY_ALLOCATION_SPREADSHEET_SLUG } from 'pages/ValuationsAllocation/common/constants/equityAllocation';
import { EQUITY_ALLOCATION_SPREADSHEET_TABLE_TERMS } from 'pages/ValuationsAllocation/common/constants/equityAllocation/sheetConfigs';
import { RedirectToFundOwnershipButton } from 'pages/ValuationsAllocation/components';
import { getObjectValue, getStringValue } from 'utilities';
import generateEquityAllocationKey from './config/util/generate-equity-allocation-key';
import useStyles from './styles';
import { EquityAllocationProps } from './types';

const EQUITY_ALLOCATION_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${EQUITY_ALLOCATION_SPREADSHEET_SLUG}-container`;

const EquityAllocation: FC<EquityAllocationProps> = props => {
  const {
    areCapTableSecuritiesInvalid,
    collapsibleColumns,
    equityAllocationRowsGroups,
    handleDeleteScenario,
    isAllocationEmpty,
    isFundOwnershipEmpty,
    isLoading,
    isLoadingValuationSummary,
    isSaving,
    measurementDate,
    onSpreadsheetChange,
    setCollapsibleColumns,
    setEquityAllocationRowsGroups,
    workbook,
    relatedEntity,
  } = props;

  const { [generateEquityAllocationKey(relatedEntity?.company_id)]: equityAllocationSpreadsheet } =
    getObjectValue(workbook);
  const { data: equityAllocationSpreadsheetData } = getObjectValue(equityAllocationSpreadsheet);

  const classes = useStyles();
  const { isExportMode } = useLayoutContext();

  const [format, formatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE }) as UseFormatValues;

  // Handle transition from GridSkeleton to Equity Allocation
  const isLoadingEquityAllocation = useMemo(
    () => isEmpty(equityAllocationSpreadsheetData),
    [equityAllocationSpreadsheetData]
  );

  // Create Equity Allocation Sheet props
  const equityAllocationSheetProps = useMemo(() => {
    if (equityAllocationSpreadsheet) {
      return {
        ...equityAllocationSpreadsheet,
        collapsibleColumns,
        deleteColumn: handleDeleteScenario,
        format,
        formatDispatch,
        onChange: onSpreadsheetChange,
        rowGroups: equityAllocationRowsGroups,
        setCollapsibleColumns,
        setRowGroups: setEquityAllocationRowsGroups,
        sheet: equityAllocationSpreadsheet,
        workbook,
      } as unknown as typeof ScalarSpreadsheet;
    }

    return null;
  }, [
    collapsibleColumns,
    equityAllocationRowsGroups,
    equityAllocationSpreadsheet,
    format,
    formatDispatch,
    handleDeleteScenario,
    onSpreadsheetChange,
    setCollapsibleColumns,
    setEquityAllocationRowsGroups,
    workbook,
  ]);

  // Extract key for direct JSX prop
  const spreadsheetKey = useMemo(
    () => getStringValue(equityAllocationSpreadsheet?.name),
    [equityAllocationSpreadsheet]
  );

  // Loading Equity Allocation
  if (isLoadingEquityAllocation || isLoadingValuationSummary) {
    return (
      <Grid className={EQUITY_ALLOCATION_CONTAINER_CLASS} size={{ xs: 12 }}>
        <GridSkeleton />
      </Grid>
    );
  }

  // Display empty Fund Ownership message if there is no Fund Ownership
  if (isFundOwnershipEmpty) {
    return (
      <Grid className={clsx(EQUITY_ALLOCATION_CONTAINER_CLASS, classes.emptyMessageContainer)} size={{ xs: 12 }}>
        <EmptyTableMessage
          component={<RedirectToFundOwnershipButton measurementDateSlug={getStringValue(measurementDate?.slug)} />}
          tableTerms={FUND_OWNERSHIP_SPREADSHEET_TABLE_TERMS}
          tagline={ALLOCATION_EMPTY_FUND_OWNERSHIP_TAGLINE}
          title={ALLOCATION_EMPTY_FUND_OWNERSHIP_TITLE}
        />
      </Grid>
    );
  }

  // Display empty Cap Table message if Cap Table Securities are invalid
  if (areCapTableSecuritiesInvalid) {
    return (
      <Grid className={clsx(EQUITY_ALLOCATION_CONTAINER_CLASS, classes.emptyMessageContainer)} size={{ xs: 12 }}>
        <EmptyCapTableMessage
          tagline={CAP_TABLE_NEEDS_MORE_SECURITIES_TAGLINE}
          title={CAP_TABLE_NEEDS_MORE_SECURITIES_TITLE}
        />
      </Grid>
    );
  }

  // Display empty Allocation message if there are no Allocation Scenarios
  if (isAllocationEmpty) {
    return (
      <Grid className={clsx(EQUITY_ALLOCATION_CONTAINER_CLASS, classes.emptyMessageContainer)} size={{ xs: 12 }}>
        <EmptyTableMessage tableTerms={EQUITY_ALLOCATION_SPREADSHEET_TABLE_TERMS} />
      </Grid>
    );
  }

  return (
    <Grid
      className={clsx(EQUITY_ALLOCATION_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}
      size={{ xs: 12 }}>
      {/* Equity Allocation */}
      <CustomScalarSpreadsheet
        key={spreadsheetKey}
        {...equityAllocationSheetProps}
        isLoading={isLoading || isLoadingEquityAllocation}
        isSaving={isSaving}
      />
    </Grid>
  );
};

export default EquityAllocation;
